import {
  AppUiUploadMode,
  AppUiUploadRequest,
} from '@omni/interfaces/app-ui/record.interface';
import { OrderDraft } from './order-draft';
import { isGuid } from '@omni/utility/dynamics-row.util';
import { roundHalfAwayFromZero } from '@omni/utility/round.util';
import { toDateTimeOffset } from '@omni/utility/iso-day.util';

/** The Dynamics write API for orders — docs/features/order-capture.md § Saving. */
export const SAVE_ORDER_ACTION = 'indskr_SaveOrder';

/**
 * Header columns never sent in `attributes` or `clearAttributes`. SaveOrder refuses the whole save
 * when one of the columns it maintains arrives (`ATTRIBUTE_NOT_ALLOWED`, doc 04 SV-010 / SV-043,
 * doc 10): state and owner (from `CapturedBy`), the request key, the totals, the currency. Status
 * moves with the request mode, `customerid` goes as a typed lookup, `pricelevelid` as `priceListId`.
 */
const HEADER_NOT_IN_ATTRIBUTES = new Set([
  'customerid',
  'pricelevelid',
  'statuscode',
  'statecode',
  // Numbered by Dynamics; shown in the header slot, never written.
  'ordernumber',
  'ownerid',
  'accountid',
  'modifiedon',
  'indskr_externalid',
  'ispricelocked',
  'skippricecalculation',
  'transactioncurrencyid',
  'exchangerate',
  'totalamount',
  'totallineitemamount',
  'totallineitemdiscountamount',
  'totaldiscountamount',
  'totalamountlessfreight',
  'totaltax',
]);

/** Configured fields the payload left out, warned about once each: a screen should not offer them. */
const warnedNotSent = new Set<string>();

/**
 * Every `_base` column is the platform's conversion into the org currency, also refused. A name
 * that is not a lowercase logical name is a client annotation (`_Formatted`, `@OData…`): SaveOrder
 * refuses the whole save over it.
 */
function isSentAttribute(attribute: string): boolean {
  return (
    /^[a-z][a-z0-9_]*$/.test(attribute) &&
    !HEADER_NOT_IN_ATTRIBUTES.has(attribute) &&
    !attribute.endsWith('_base')
  );
}

/** Keys a generated reward line apart from a bought line of the same product and delivery. */
export const REWARD_LINE_SUFFIX = '#reward';

export interface SaveOrderInput {
  draft: OrderDraft;
  accountId: string;
  /** `systemuserid` of the rep. Required: under S2S the caller is the application user. */
  capturedBy: string;
  mode: AppUiUploadMode;
  /** Line ids earlier requests carried; any no longer on the order is named as removed. */
  previouslySentLineIds: readonly string[];
  /** What the server holds from the last request that landed; null sends the whole order. */
  acknowledged?: SaveOrderAcknowledged | null;
  /**
   * What requests sent since then would leave on the server; any may have landed. A line is skipped
   * as unchanged only when all of them agree, and whatever any of them carries can be removed.
   */
  sentSinceAcknowledged?: readonly unknown[];
  /**
   * The product's own default unit, for a price-list item that names none. Dynamics refuses a
   * catalogue line with no unit, and does it mid-write, so the save faults rather than refusing.
   */
  defaultUomOf?: (product: string) => string | null;
}

/**
 * The order as the server holds it once a request lands: a fingerprint of every line and delivery
 * it carried, the header columns it set, and each line's number. The next request is a delta
 * against this, so it must move only on a landing — a refused or unsent request changed nothing.
 */
export interface SaveOrderAcknowledged {
  lines: Record<string, string>;
  deliveries: Record<string, string>;
  attributes: string[];
  lineNumbers: Record<string, number>;
}

export interface SaveOrderBuild {
  request: AppUiUploadRequest;
  /** The line ids this request carries, for the draft to remember. */
  lineIds: string[];
}

/** A baseline this builder wrote; anything else is treated as none, so the whole order is sent. */
function isAcknowledged(value: unknown): value is SaveOrderAcknowledged {
  const ack = value as Partial<SaveOrderAcknowledged> | null | undefined;
  return (
    !!ack &&
    typeof ack.lines === 'object' &&
    !!ack.lines &&
    typeof ack.deliveries === 'object' &&
    !!ack.deliveries &&
    Array.isArray(ack.attributes) &&
    typeof ack.lineNumbers === 'object' &&
    !!ack.lineNumbers
  );
}

/**
 * FNV-1a over the JSON: a short, stable token for "has this row changed". FNV-1a is defined by
 * XOR; the tokens are stored on devices as the delta baseline, so the algorithm must not change.
 */
/* eslint-disable no-bitwise */
function fingerprint(value: unknown): string {
  let hash = 0x811c9dc5;
  for (const char of JSON.stringify(value)) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}
/* eslint-enable no-bitwise */

/** Lines keep the number the server already has; new ones follow the highest ever given. */
function numberLines(
  ids: readonly string[],
  known: Record<string, number> | undefined,
): Record<string, number> {
  const numbers: Record<string, number> = {};
  let next = Math.max(0, ...Object.values(known ?? {}));
  for (const id of ids) numbers[id] = known?.[id] ?? ++next;
  return numbers;
}

function roundTo(value: number, precision: number): number {
  return roundHalfAwayFromZero(value, precision);
}

type DraftDelivery = OrderDraft['deliveries'][number];

/** Deliveries as the server reads them; `sequence` is their date order (1..n, undated last), as the PCF numbers them. */
function toPayloadDeliveries(deliveries: readonly DraftDelivery[]) {
  const byDate = [...deliveries].sort(
    (a, b) =>
      (a.date ?? '\uffff').localeCompare(b.date ?? '\uffff') ||
      a.sequence - b.sequence,
  );
  const position = new Map(
    byDate.map((delivery, index) => [delivery.id, index + 1]),
  );
  return deliveries.map((delivery) => ({
    id: delivery.id,
    // indskr_deliverydate is Date Only: an offset would shift it a day, so it stays a calendar date.
    deliveryDate: delivery.date ?? null,
    sequence: position.get(delivery.id) ?? delivery.sequence,
    shipToId: delivery.shipTo ?? null,
    isPrimary: delivery.isPrimary,
  }));
}

function toPayloadLines(
  input: SaveOrderInput,
  knownNumbers: Record<string, number>,
) {
  const { draft } = input;
  // The engine's own: rounding again to any other precision would break price × quantity.
  const precision = draft.priced.currencyPrecision;
  const itemsById = new Map(
    (draft.priceList.items ?? []).map((item) => [item.id, item]),
  );
  // A bundle synced before plug-in 1.0.0.4 names the unit in `uom`; the server takes only its id.
  const unitId = (priceListItemId: string | null, product: string) => {
    const uom = priceListItemId ? itemsById.get(priceListItemId)?.uom : null;
    return (isGuid(uom) ? uom : input.defaultUomOf?.(product)) || null;
  };
  const deliveryIdBySequence = new Map(
    draft.deliveries.map((delivery) => [delivery.sequence, delivery.id]),
  );
  const unnumbered = draft.priced.lines.flatMap((line) =>
    line.perDelivery
      .filter((cell) => cell.quantity > 0)
      .map((cell) => {
        const baseAmount = roundTo(cell.gross, precision);
        const extendedAmount = roundTo(cell.net, precision);
        return {
          // A reward line can share its product and delivery with a bought line; it is a second line.
          id: draft.lineId(
            line.isFreeGood
              ? `${line.product}${REWARD_LINE_SUFFIX}`
              : line.product,
            cell.sequence,
          ),
          productId: line.product,
          deliveryId: deliveryIdBySequence.get(cell.sequence) ?? null,
          priceListItemId: line.priceListItemId,
          uomId: unitId(line.priceListItemId, line.product),
          quantity: cell.quantity,
          // A reward is given, not sold: its price is zero so price × quantity matches its zero base.
          pricePerUnit: line.isFreeGood ? 0 : line.unitPrice,
          baseAmount,
          // Derived after rounding so base − discount + tax balances exactly, as the parser checks.
          manualDiscountAmount: Math.max(
            0,
            roundTo(baseAmount - extendedAmount, precision),
          ),
          tax: 0,
          extendedAmount,
          isFreeGood: line.isFreeGood,
        };
      }),
  );
  const lineNumbers = numberLines(
    unnumbered.map((line) => line.id),
    knownNumbers,
  );
  return {
    lineNumbers,
    lines: unnumbered.map((line) => ({
      ...line,
      lineItemNumber: lineNumbers[line.id],
    })),
  };
}

/**
 * The engine's firings as `appliedDiscounts`, each allocation naming the line it touched. The
 * request is built once and queued, so a retry carries the same firing ids and cannot double-write.
 */
function toPayloadFirings(draft: OrderDraft) {
  const lineOf = (product: string, sequence: number, freeGood: boolean) =>
    draft.lineId(
      freeGood ? `${product}${REWARD_LINE_SUFFIX}` : product,
      sequence,
    );
  return draft.priced.firings.map((firing, index) => ({
    id: crypto.randomUUID(),
    seq: index + 1,
    ...(firing.ruleId ? { ruleId: firing.ruleId } : {}),
    ...(firing.freeGoodId ? { freeGoodsId: firing.freeGoodId } : {}),
    ...(firing.productId ? { productId: firing.productId } : {}),
    baseAmount: firing.baseAmount,
    amount: firing.amount,
    ...(firing.uncappedAmount !== null
      ? { uncappedAmount: firing.uncappedAmount }
      : {}),
    evalQty: firing.evaluatedQuantity,
    ...(firing.rewardQuantity !== null
      ? { rewardQty: firing.rewardQuantity }
      : {}),
    ...(firing.repeatCount !== null ? { repeatCount: firing.repeatCount } : {}),
    alloc: firing.allocations.map((a) => ({
      l: lineOf(a.product, a.sequence, a.freeGood),
      r: a.role,
      q: a.quantity,
      a: a.amount,
    })),
    qualifyingRuleIds: firing.qualifyingRuleIds,
    qualifyingFreeGoodsIds: firing.qualifyingFreeGoodIds,
  }));
}

/** Header columns to set, and those the server holds that must now be cleared. */
function toPayloadAttributes(
  input: SaveOrderInput,
  primaryDate: string | null | undefined,
  heldAttributes: readonly string[],
) {
  const { draft } = input;
  const customerId = String(draft.header.customerid?.value ?? input.accountId);
  const attributes: Record<string, unknown> = {
    customerid: { id: customerId, type: 'account' },
    ...(primaryDate
      ? { requestdeliveryby: toDateTimeOffset(primaryDate) }
      : {}),
  };
  for (const [attribute, value] of Object.entries(draft.header)) {
    if (!isSentAttribute(attribute)) {
      warnNotSentOnce(attribute, value.value);
      continue;
    }
    // A lookup binds to its table; a bare id cannot say which one and the parser drops it (SV-005).
    attributes[attribute] =
      value.entity && value.value !== null
        ? { id: String(value.value), type: value.entity }
        : value.value;
  }
  const clearedBySchedule = applyScheduleColumns(
    attributes,
    draft.schedule?.columns ?? {},
  );
  // A header column the server holds and the draft no longer sets must be cleared: omitting it means "unchanged".
  const clearAttributes = [
    ...new Set([...heldAttributes, ...clearedBySchedule]),
  ].filter(
    (attribute) => !(attribute in attributes) && isSentAttribute(attribute),
  );
  return { attributes, clearAttributes };
}

function warnNotSentOnce(attribute: string, value: unknown): void {
  if (
    attribute === 'customerid' ||
    warnedNotSent.has(attribute) ||
    value === null ||
    value === undefined
  )
    return;
  warnedNotSent.add(attribute);
  console.warn(
    `SaveOrder: '${attribute}' is maintained by Dynamics and is not sent`,
  );
}

/**
 * The schedule is copied onto the order and what it leaves empty is cleared, so an order never
 * carries a blend of two cadences — whatever set those columns before. Returns the cleared columns.
 */
function applyScheduleColumns(
  attributes: Record<string, unknown>,
  columns: Record<string, unknown>,
): string[] {
  const cleared: string[] = [];
  for (const [attribute, value] of Object.entries(columns)) {
    if (!isSentAttribute(attribute)) continue;
    if (value === null || value === undefined) {
      delete attributes[attribute];
      cleared.push(attribute);
    } else {
      attributes[attribute] = value;
    }
  }
  return cleared;
}

/**
 * The `indskr_SaveOrder` request for a draft, in the key names the live parser reads. After the
 * first landing it is a delta: only new or changed lines and deliveries, with what the server holds
 * and the draft no longer does named in `removedLineIds`, `removedDeliveryIds` and
 * `order.clearAttributes`. Every line costs the server about a second to write, so re-sending an
 * unchanged order is what a delta saves. Ids were minted on the device, so a retried create cannot
 * duplicate and a repeated delete is a success. `appliedDiscounts` goes with a submit only: the
 * server records provenance then and ignores it on a save.
 */
export function buildSaveOrderRequest(input: SaveOrderInput): SaveOrderBuild {
  const { draft, mode } = input;
  const known = isAcknowledged(input.acknowledged) ? input.acknowledged : null;
  const outstanding = (input.sentSinceAcknowledged ?? []).filter(
    isAcknowledged,
  );
  const held = known ? [known, ...outstanding] : outstanding;
  const primary =
    draft.deliveries.find((d) => d.isPrimary) ?? draft.deliveries[0];
  const { lines, lineNumbers } = toPayloadLines(
    input,
    Object.assign({}, ...held.map((ack) => ack.lineNumbers)),
  );

  // A delivery with no quantities is not sent, but the primary always is: an empty order still needs one.
  // So is a draft's drop whose date the rep picked; a submitted order keeps only real deliveries.
  const usedDeliveryIds = new Set(lines.map((line) => line.deliveryId));
  const deliveries = toPayloadDeliveries(
    draft.deliveries.filter(
      (delivery) =>
        delivery.id === primary?.id ||
        usedDeliveryIds.has(delivery.id) ||
        (mode !== 'submit' && !!delivery.datePicked),
    ),
  );

  const lineIds = lines.map((line) => line.id);
  const current = new Set(lineIds);
  // After a landing the baseline is exact; before one, every id a request carried is named.
  const removedLineIds = [
    ...new Set([
      ...(known ? [] : input.previouslySentLineIds),
      ...held.flatMap((ack) => Object.keys(ack.lines)),
    ]),
  ].filter((id) => !current.has(id));
  // A dropped delivery's lines are all in removedLineIds, so the server's emptiness check holds.
  const sentDeliveryIds = new Set(deliveries.map((delivery) => delivery.id));
  const removedDeliveryIds = [
    ...new Set(held.flatMap((ack) => Object.keys(ack.deliveries))),
  ].filter((id) => !sentDeliveryIds.has(id));

  const linePrints = Object.fromEntries(
    lines.map((line) => [line.id, fingerprint(line)]),
  );
  const deliveryPrints = Object.fromEntries(
    deliveries.map((delivery) => [delivery.id, fingerprint(delivery)]),
  );
  const { attributes, clearAttributes } = toPayloadAttributes(
    input,
    primary?.date,
    held.flatMap((ack) => ack.attributes),
  );
  // Unchanged only when the server holds this print whichever of the sent requests landed.
  const unchanged = (kind: 'lines' | 'deliveries', id: string, print: string) =>
    !!known && held.every((ack) => ack[kind][id] === print);

  const payload = {
    order: {
      id: draft.id,
      priceListId: draft.priceList.id,
      // The list's own version, never the bundle hash: another list republishing must not refuse this order.
      priceListVersion: draft.priceList.version,
      attributes,
      ...(clearAttributes.length ? { clearAttributes } : {}),
    },
    deliveries: deliveries.filter(
      (delivery) =>
        !unchanged('deliveries', delivery.id, deliveryPrints[delivery.id]),
    ),
    lines: lines.filter(
      (line) => !unchanged('lines', line.id, linePrints[line.id]),
    ),
    removedLineIds,
    removedDeliveryIds,
    appliedDiscounts: mode === 'submit' ? toPayloadFirings(draft) : [],
  };
  const acknowledges: SaveOrderAcknowledged = {
    lines: linePrints,
    deliveries: deliveryPrints,
    attributes: Object.keys(attributes),
    lineNumbers,
  };

  const clientRequestId = `goapp-${draft.id}`;
  return {
    lineIds,
    request: {
      action: SAVE_ORDER_ACTION,
      mode,
      requestId: clientRequestId,
      acknowledges,
      /* eslint-disable @typescript-eslint/naming-convention -- the Custom API's parameter names */
      params: {
        Payload: JSON.stringify(payload),
        ClientRequestId: clientRequestId,
        CapturedBy: input.capturedBy,
        Submit: mode === 'submit',
        Mode: 'Commit',
      },
      /* eslint-enable @typescript-eslint/naming-convention */
    },
  };
}

/**
 * The `indskr_SaveOrder` call that cancels an order with its lines and deliveries (contract v1.20).
 * Scrap refuses a payload carrying anything but `order.id`, and answers `Scrapped` again on a retry.
 */
export function buildScrapOrderRequest(
  orderId: string,
  capturedBy: string,
): { action: string; params: Record<string, unknown> } {
  return {
    action: SAVE_ORDER_ACTION,
    /* eslint-disable @typescript-eslint/naming-convention -- the Custom API's parameter names */
    params: {
      Payload: JSON.stringify({ order: { id: orderId } }),
      ClientRequestId: `goapp-scrap-${crypto.randomUUID()}`,
      CapturedBy: capturedBy,
      Submit: false,
      Mode: 'Scrap',
    },
    /* eslint-enable @typescript-eslint/naming-convention */
  };
}
